import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useFormatters } from '@siteshowroom/i18n/react'
import type { Formatters } from '@siteshowroom/i18n'
import { useTranslate } from '../lib/i18n-provider'
import type { TranslateFn } from '../lib/i18n-provider'
import {
  bookingErrorMessageKey,
  cancelBooking,
  confirmBooking,
  declineBooking,
  describeHold,
  extendBooking,
  formatCentsForInput,
  listBookings,
  parseAmountToCents,
  sortByArrival,
  sortByDeadline,
} from '../lib/bookings-client'
import type {
  Booking,
  BookingFailure,
  BookingPrice,
  BookingResult,
  ConfirmInput,
  RefundEntitlement,
  HoldCountdown,
  HoldUnit,
} from '../lib/bookings-client'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'

/**
 * De werkvoorraad van de verhuurder.
 *
 * De volgorde op dit scherm is de boodschap. Openstaande aanvragen staan
 * bovenaan, met hun resterende termijn erbij, omdat een aanvraag die blijft
 * liggen de agenda blokkeert (§17.13): de `hold` houdt de nachten bezet met
 * dezelfde `EXCLUDE`-constraint als een bevestigde reservering. Wie deze lijst
 * niet afwerkt, verkoopt die nachten aan niemand. Bevestigde reserveringen staan
 * eronder; die vragen niets meer.
 *
 * Drie keuzes die niet vrijblijvend zijn:
 *
 * 1. **Bevestigen is een tweetrapsactie.** De knop opent geen bevestiging van het
 *    formulier maar van de toezegging: bevestigen belooft een gast een woning, en
 *    terugdraaien kan daarna alleen als annulering met reden. Bewust geen
 *    `window.confirm` zoals bij het verwijderen van een foto: die dialoog kan het
 *    bedrag en de notitie die worden meegestuurd niet tonen, en juist die twee
 *    velden horen bij het besluit.
 * 2. **Na iedere actie wordt de lijst opnieuw opgehaald,** ook wanneer de actie
 *    mislukte. De API is de bron; een aanvraag kan intussen verlopen zijn of in
 *    een ander tabblad zijn afgehandeld, en dan hoort dit scherm dat te weten in
 *    plaats van een status te tonen die de database niet heeft.
 * 3. **Er wordt geen betaalmodus geraden.** Meldt de API er geen, dan toont het
 *    scherm er geen (CLAUDE.md regel 9). "Online betaald" tonen bij een
 *    handmatige aanvraag is precies de verwarring die §17.13 uitsluit.
 */

/** Hoe vaak de resterende termijn opnieuw wordt uitgerekend. */
const CLOCK_INTERVAL_MS = 30_000

/**
 * Verlengingen die de verhuurder kan kiezen, in uren. Bewust een korte lijst en
 * geen vrij invoerveld: verlengen rekt de blokkade van de agenda op, en 72 uur —
 * de voorgestelde standaardtermijn van een aanvraag (§17.13) — is de bovengrens
 * die hier zonder nadenken te kiezen valt. Eén dag staat vooraan, omdat dat de
 * gebruikelijke ruimte is voor een overschrijving die onderweg is.
 */
const EXTEND_HOUR_OPTIONS: readonly number[] = [24, 48, 72]
const DEFAULT_EXTEND_HOURS = 24

type ResidencesState =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly residences: readonly ResidenceOption[] }
  | { readonly phase: 'error'; readonly error: BookingFailure }

interface BookingLists {
  readonly pending: readonly Booking[]
  readonly confirmed: readonly Booking[]
}

type BookingsState =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready'; readonly lists: BookingLists }
  | { readonly phase: 'error'; readonly error: BookingFailure }

/** Welk formulier op een aanvraagkaart open staat. */
type Panel = 'none' | 'confirm' | 'decline' | 'extend'

// ---------------------------------------------------------------------------
// Kleine hulpjes
// ---------------------------------------------------------------------------

/**
 * De klok waarop de aftelling meeloopt.
 *
 * Zonder deze tik blijft "nog 3 uur" staan tot iemand de pagina ververst, en dat
 * is precies het getal waarop een verhuurder zijn dag indeelt.
 */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState<number>(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now())
    }, intervalMs)
    return () => {
      window.clearInterval(timer)
    }
  }, [intervalMs])
  return now
}

/**
 * Een verblijfsdatum is een kalenderdatum in de tijdzone van de residence, geen
 * tijdstip. Hij wordt daarom als UTC-middernacht gelezen én in UTC geformatteerd:
 * zou de browser hem in zijn eigen zone tonen, dan verschuift 25 augustus in
 * Los Angeles naar 24 augustus — een nacht verschil op het scherm.
 */
function formatCalendarDate(formatters: Formatters, value: string): string {
  return formatters.formatDate(`${value}T00:00:00Z`, { timeZone: 'UTC' })
}

/** De referentie zoals de gast hem kent; ontbreekt hij, dan is een kort id de herkenning. */
function bookingReference(booking: Booking): string {
  return booking.reference === '' ? booking.id.slice(0, 8) : booking.reference
}

/**
 * Waaronder de gast op het scherm staat: zijn naam, anders zijn e-mailadres. Is
 * de gast gewist of geanonimiseerd, dan zegt het scherm dat met zoveel woorden —
 * de reservering blijft immers bestaan (§17.11, `guests.anonymized_at`).
 */
function guestLabel(t: TranslateFn, booking: Booking): string {
  if (booking.guest === null) {
    return t('dashboard.bookings.guestUnknown')
  }
  return booking.guest.fullName === '' ? booking.guest.email : booking.guest.fullName
}

function deadlineMessageKey(unit: HoldUnit): string {
  switch (unit) {
    case 'minute':
      return 'dashboard.bookings.deadline.minutes'
    case 'hour':
      return 'dashboard.bookings.deadline.hours'
    case 'day':
      return 'dashboard.bookings.deadline.days'
  }
}

/** Vertaalde melding plus het onvertaalde technische detail ernaast. */
function FailureNotice({ error }: { readonly error: BookingFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(bookingErrorMessageKey(error))}</p>
      {/* Diagnose-informatie, bewust onvertaald — zelfde keuze als op de systeempagina. */}
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

/**
 * De resterende termijn, met de urgentie in een data-attribuut zodat de opmaak
 * hem kan markeren. `data-tone` kent vier waarden: verlopen, dringend (minder dan
 * een halve dag), bijna (minder dan een dag) en gewoon.
 */
function DeadlineChip({
  countdown,
  expiresAt,
}: {
  readonly countdown: HoldCountdown | null
  readonly expiresAt: string | null
}): ReactElement | null {
  const t = useTranslate()
  const formatters = useFormatters()

  if (countdown === null) {
    return null
  }

  const label =
    countdown.tone === 'expired'
      ? t('dashboard.bookings.deadline.expired')
      : t(deadlineMessageKey(countdown.unit), { count: countdown.count })

  return (
    <p className="deadline" data-tone={countdown.tone}>
      <span className="deadline__mark" aria-hidden="true" />
      <span className="deadline__label">{label}</span>
      {expiresAt !== null && (
        <span className="deadline__moment">
          {t('dashboard.bookings.deadline.until', {
            moment: formatters.formatDateTime(expiresAt),
          })}
        </span>
      )}
    </p>
  )
}

/**
 * De prijsopbouw zoals bij het boeken vastgelegd. Onderdelen die nul zijn worden
 * weggelaten: een regel "extra gasten € 0,00" zegt niets en maakt het totaal
 * moeilijker te lezen. De korting staat er als negatief bedrag, want zo telt zij.
 */
function PriceBreakdown({ price }: { readonly price: BookingPrice }): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const rows: { readonly key: string; readonly cents: number }[] = []
  if (price.nightsTotalCents > 0) {
    rows.push({ key: 'dashboard.bookings.price.nights', cents: price.nightsTotalCents })
  }
  if (price.cleaningFeeCents > 0) {
    rows.push({ key: 'dashboard.bookings.price.cleaning', cents: price.cleaningFeeCents })
  }
  if (price.extraGuestCents > 0) {
    rows.push({ key: 'dashboard.bookings.price.extraGuest', cents: price.extraGuestCents })
  }
  if (price.discountCents > 0) {
    rows.push({ key: 'dashboard.bookings.price.discount', cents: -price.discountCents })
  }
  if (price.touristTaxCents > 0) {
    // Apart in de uitsplitsing: het staat in het totaal maar is geen omzet van de
    // verhuurder — die draagt het af (§17.24).
    rows.push({ key: 'dashboard.bookings.price.touristTax', cents: price.touristTaxCents })
  }

  return (
    <dl className="price-list">
      {rows.map((row) => (
        <div className="price-list__row" key={row.key}>
          <dt>{t(row.key)}</dt>
          <dd>{formatters.formatMoney(row.cents, price.currency)}</dd>
        </div>
      ))}
      <div className="price-list__row price-list__row--total">
        <dt>{t('dashboard.bookings.price.total')}</dt>
        <dd>{formatters.formatMoney(price.totalCents, price.currency)}</dd>
      </div>
      {price.depositCents > 0 && (
        <div className="price-list__row price-list__row--aside">
          <dt>{t('dashboard.bookings.price.deposit')}</dt>
          <dd>{formatters.formatMoney(price.depositCents, price.currency)}</dd>
        </div>
      )}
    </dl>
  )
}

// ---------------------------------------------------------------------------
// De aanvraagkaart
// ---------------------------------------------------------------------------

interface RequestCardProps {
  readonly booking: Booking
  readonly busy: boolean
  readonly now: number
  readonly onConfirm: (booking: Booking, input: ConfirmInput) => void
  readonly onDecline: (booking: Booking, reason: string) => void
  readonly onExtend: (booking: Booking, hours: number) => void
}

function RequestCard({
  booking,
  busy,
  now,
  onConfirm,
  onDecline,
  onExtend,
}: RequestCardProps): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()

  const [panel, setPanel] = useState<Panel>('none')
  /** Tweede trap van het bevestigen: de toezegging, niet het formulier. */
  const [verifying, setVerifying] = useState(false)
  const [amount, setAmount] = useState<string>(() => formatCentsForInput(booking.price.totalCents))
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const [hours, setHours] = useState<number>(DEFAULT_EXTEND_HOURS)
  /** Vertaalsleutel van het probleem in het formulier, nooit letterlijke tekst. */
  const [problemKey, setProblemKey] = useState<string | null>(null)

  const countdown = describeHold(booking.holdExpiresAt, now)
  const reference = bookingReference(booking)
  const guestName = guestLabel(t, booking)
  const dates = t('dashboard.bookings.dates', {
    checkIn: formatCalendarDate(formatters, booking.checkIn),
    checkOut: formatCalendarDate(formatters, booking.checkOut),
  })

  function openPanel(next: Panel): void {
    setPanel((current) => (current === next ? 'none' : next))
    setVerifying(false)
    setProblemKey(null)
  }

  /** Leeg laten mag: bevestigen zonder bedrag vast te leggen is geldig (§17.13). */
  function readAmountCents():
    { readonly ok: true; readonly cents: number | null } | { readonly ok: false } {
    const trimmed = amount.trim()
    if (trimmed === '') {
      return { ok: true, cents: null }
    }
    const cents = parseAmountToCents(trimmed)
    return cents === null ? { ok: false } : { ok: true, cents }
  }

  function handleConfirmSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const parsed = readAmountCents()
    if (!parsed.ok) {
      setProblemKey('dashboard.bookings.confirm.amountInvalid')
      return
    }
    setProblemKey(null)
    setVerifying(true)
  }

  function handleConfirmVerified(): void {
    const parsed = readAmountCents()
    if (!parsed.ok) {
      setProblemKey('dashboard.bookings.confirm.amountInvalid')
      setVerifying(false)
      return
    }
    // `exactOptionalPropertyTypes`: een veld weglaten is iets anders dan het op
    // `undefined` zetten, en alleen het eerste betekent "geen bedrag vastleggen".
    const input: ConfirmInput =
      parsed.cents === null ? { note } : { amountCents: parsed.cents, note }
    onConfirm(booking, input)
  }

  function handleDeclineSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const trimmed = reason.trim()
    if (trimmed === '') {
      setProblemKey('dashboard.bookings.decline.required')
      return
    }
    setProblemKey(null)
    onDecline(booking, trimmed)
  }

  function handleExtendSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    onExtend(booking, hours)
  }

  const problem =
    problemKey === null ? null : (
      <p className="request-panel__problem" role="alert">
        {t(problemKey)}
      </p>
    )

  return (
    <li className="request-card" data-tone={countdown === null ? 'unknown' : countdown.tone}>
      <div className="request-card__header">
        <div>
          {/* Naam en referentie zijn data uit de database; alleen de terugval bij
              een gewiste gast en het omhullende zinnetje komen uit de catalogus. */}
          <h3 className="request-card__title">{guestName}</h3>
          <p className="request-card__reference">
            {t('dashboard.bookings.reference', { reference })}
          </p>
        </div>
        <DeadlineChip countdown={countdown} expiresAt={booking.holdExpiresAt} />
      </div>

      <dl className="facts">
        <div className="facts__item">
          <dt>{t('dashboard.bookings.column.dates')}</dt>
          <dd>
            {dates}
            <span className="facts__aside">
              {t('dashboard.bookings.nights', { count: booking.nights })}
            </span>
          </dd>
        </div>
        <div className="facts__item">
          <dt>{t('dashboard.bookings.column.guests')}</dt>
          <dd>
            {booking.guests === null
              ? t('dashboard.bookings.guestsUnknown')
              : t('dashboard.bookings.guests', { count: booking.guests })}
          </dd>
        </div>
        {booking.guest !== null && booking.guest.email !== '' && (
          <div className="facts__item">
            <dt>{t('dashboard.bookings.column.contact')}</dt>
            <dd>
              {booking.guest.email}
              {booking.guest.phone !== '' && (
                <span className="facts__aside">{booking.guest.phone}</span>
              )}
            </dd>
          </div>
        )}
        {booking.paymentMode !== null && (
          <div className="facts__item">
            <dt>{t('dashboard.bookings.column.payment')}</dt>
            <dd>
              {booking.paymentMode === 'manual'
                ? t('dashboard.bookings.paymentMode.manual')
                : t('dashboard.bookings.paymentMode.online')}
            </dd>
          </div>
        )}
        {booking.createdAt !== null && (
          <div className="facts__item">
            <dt>{t('dashboard.bookings.column.requested')}</dt>
            <dd>{formatters.formatDateTime(booking.createdAt)}</dd>
          </div>
        )}
      </dl>

      <PriceBreakdown price={booking.price} />

      {booking.guestMessage !== '' && (
        <blockquote className="request-card__message">
          <p className="request-card__messageLabel">{t('dashboard.bookings.message')}</p>
          {/* Tekst van de gast: data. */}
          <p>{booking.guestMessage}</p>
        </blockquote>
      )}

      <div className="request-card__actions">
        <button
          aria-expanded={panel === 'confirm'}
          className="button button--primary"
          disabled={busy}
          onClick={() => {
            openPanel('confirm')
          }}
          type="button"
        >
          {t('dashboard.bookings.action.confirm')}
        </button>
        <button
          aria-expanded={panel === 'decline'}
          className="button"
          disabled={busy}
          onClick={() => {
            openPanel('decline')
          }}
          type="button"
        >
          {t('dashboard.bookings.action.decline')}
        </button>
        <button
          aria-expanded={panel === 'extend'}
          className="button"
          disabled={busy}
          onClick={() => {
            openPanel('extend')
          }}
          type="button"
        >
          {t('dashboard.bookings.action.extend')}
        </button>
      </div>

      {panel === 'confirm' && !verifying && (
        <form className="request-panel" onSubmit={handleConfirmSubmit}>
          <h4 className="request-panel__title">{t('dashboard.bookings.confirm.title')}</h4>
          <p className="request-panel__hint">{t('dashboard.bookings.confirm.intro')}</p>
          <label className="field">
            {/* De valuta staat in het label en niet in het veld: het veld draagt
                een geheel aantal centen, geen opmaak. */}
            <span className="field__label">
              {t('dashboard.bookings.confirm.amount', { currency: booking.price.currency })}
            </span>
            <input
              className="field__input"
              disabled={busy}
              inputMode="decimal"
              min="0"
              onChange={(event) => {
                setAmount(event.target.value)
              }}
              step="0.01"
              type="number"
              value={amount}
            />
            <span className="field__hint">{t('dashboard.bookings.confirm.amountHint')}</span>
          </label>
          <label className="field">
            <span className="field__label">{t('dashboard.bookings.confirm.note')}</span>
            <textarea
              className="field__input"
              disabled={busy}
              onChange={(event) => {
                setNote(event.target.value)
              }}
              rows={2}
              value={note}
            />
            <span className="field__hint">{t('dashboard.bookings.confirm.noteHint')}</span>
          </label>
          {problem}
          <div className="request-panel__actions">
            <button className="button button--primary" disabled={busy} type="submit">
              {t('dashboard.bookings.confirm.submit')}
            </button>
          </div>
        </form>
      )}

      {/* Geen `role="group"` op het blok hieronder: een groep zonder toegankelijke
          naam voegt voor een schermlezer niets toe, de kop erin wel. */}
      {panel === 'confirm' && verifying && (
        <div className="request-panel request-panel--verify">
          <h4 className="request-panel__title">
            {t('dashboard.bookings.confirm.verify.title', { guest: guestName, dates })}
          </h4>
          <p className="request-panel__hint">{t('dashboard.bookings.confirm.verify.body')}</p>
          {problem}
          <div className="request-panel__actions">
            <button
              className="button button--primary"
              disabled={busy}
              onClick={handleConfirmVerified}
              type="button"
            >
              {busy
                ? t('dashboard.bookings.confirm.submitting')
                : t('dashboard.bookings.confirm.verify.submit')}
            </button>
            <button
              className="button"
              disabled={busy}
              onClick={() => {
                setVerifying(false)
              }}
              type="button"
            >
              {t('dashboard.bookings.action.back')}
            </button>
          </div>
        </div>
      )}

      {panel === 'decline' && (
        <form className="request-panel" onSubmit={handleDeclineSubmit}>
          <h4 className="request-panel__title">{t('dashboard.bookings.decline.title')}</h4>
          <label className="field">
            <span className="field__label">{t('dashboard.bookings.decline.reason')}</span>
            <textarea
              className="field__input"
              disabled={busy}
              onChange={(event) => {
                setReason(event.target.value)
              }}
              rows={2}
              value={reason}
            />
            <span className="field__hint">{t('dashboard.bookings.decline.reasonHint')}</span>
          </label>
          {problem}
          <div className="request-panel__actions">
            <button className="button button--danger" disabled={busy} type="submit">
              {busy
                ? t('dashboard.bookings.decline.submitting')
                : t('dashboard.bookings.decline.submit')}
            </button>
          </div>
        </form>
      )}

      {panel === 'extend' && (
        <form className="request-panel" onSubmit={handleExtendSubmit}>
          <h4 className="request-panel__title">{t('dashboard.bookings.extend.title')}</h4>
          <p className="request-panel__hint">{t('dashboard.bookings.extend.hint')}</p>
          <label className="field field--inline">
            <span className="field__label">{t('dashboard.bookings.extend.hours')}</span>
            <select
              className="field__input"
              disabled={busy}
              onChange={(event) => {
                setHours(Number(event.target.value))
              }}
              value={String(hours)}
            >
              {EXTEND_HOUR_OPTIONS.map((option) => (
                <option key={option} value={String(option)}>
                  {t('dashboard.bookings.extend.option', { count: option })}
                </option>
              ))}
            </select>
          </label>
          <div className="request-panel__actions">
            <button className="button" disabled={busy} type="submit">
              {busy
                ? t('dashboard.bookings.extend.submitting')
                : t('dashboard.bookings.extend.submit')}
            </button>
          </div>
        </form>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// De bevestigde reserveringen
// ---------------------------------------------------------------------------

interface ConfirmedRowProps {
  readonly booking: Booking
  readonly busy: boolean
  readonly onCancel: (booking: Booking, reason: string) => void
}

function ConfirmedRow({ booking, busy, onCancel }: ConfirmedRowProps): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [problemKey, setProblemKey] = useState<string | null>(null)

  const guestName = guestLabel(t, booking)

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const trimmed = reason.trim()
    if (trimmed === '') {
      setProblemKey('dashboard.bookings.cancel.required')
      return
    }
    setProblemKey(null)
    onCancel(booking, trimmed)
  }

  return (
    <>
      <tr>
        <td>
          {t('dashboard.bookings.dates', {
            checkIn: formatCalendarDate(formatters, booking.checkIn),
            checkOut: formatCalendarDate(formatters, booking.checkOut),
          })}
          <span className="facts__aside">
            {t('dashboard.bookings.nights', { count: booking.nights })}
          </span>
        </td>
        <td>
          {guestName}
          <span className="facts__aside">
            {t('dashboard.bookings.reference', {
              reference: bookingReference(booking),
            })}
          </span>
        </td>
        <td className="table__number">
          {booking.guests === null
            ? t('dashboard.bookings.guestsUnknown')
            : formatters.formatNumber(booking.guests)}
        </td>
        <td className="table__number">
          {formatters.formatMoney(booking.price.totalCents, booking.price.currency)}
          {/* §17.13: een handmatig bedrag is door de verhuurder opgegeven en niet
              door een provider bevestigd. Dat onderscheid hoort zichtbaar te
              blijven, anders suggereert het bedrag een zekerheid die er niet is. */}
          {booking.paidCents !== null && (
            <span className="facts__aside">
              {t('dashboard.bookings.payment.received', {
                amount: formatters.formatMoney(booking.paidCents, booking.price.currency),
              })}
            </span>
          )}
          {booking.paidCents !== null && booking.paymentMode === 'manual' && (
            <span className="facts__aside">{t('dashboard.bookings.payment.manualNote')}</span>
          )}
        </td>
        <td>
          <button
            aria-expanded={open}
            className="button button--danger"
            disabled={busy}
            onClick={() => {
              setOpen((current) => !current)
              setProblemKey(null)
            }}
            type="button"
          >
            {t('dashboard.bookings.action.cancel')}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <form className="request-panel" onSubmit={handleSubmit}>
              <h4 className="request-panel__title">{t('dashboard.bookings.cancel.title')}</h4>
              <p className="request-panel__hint">{t('dashboard.bookings.cancel.intro')}</p>
              <label className="field">
                <span className="field__label">{t('dashboard.bookings.cancel.reason')}</span>
                <textarea
                  className="field__input"
                  disabled={busy}
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                  rows={2}
                  value={reason}
                />
              </label>
              {problemKey !== null && (
                <p className="request-panel__problem" role="alert">
                  {t(problemKey)}
                </p>
              )}
              <div className="request-panel__actions">
                <button className="button button--danger" disabled={busy} type="submit">
                  {busy
                    ? t('dashboard.bookings.cancel.submitting')
                    : t('dashboard.bookings.cancel.submit')}
                </button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// De pagina
// ---------------------------------------------------------------------------

export function BookingsPage(): ReactElement {
  const t = useTranslate()
  const formatters = useFormatters()
  const now = useNow(CLOCK_INTERVAL_MS)

  const [residencesState, setResidencesState] = useState<ResidencesState>({ phase: 'loading' })
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [bookingsState, setBookingsState] = useState<BookingsState>({ phase: 'idle' })
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<BookingFailure | null>(null)
  /** Wat de gast na de laatste annulering terugkrijgt; null zolang er niets is. */
  const [refund, setRefund] = useState<RefundEntitlement | null>(null)
  const [refundCurrency, setRefundCurrency] = useState('')

  const residencesControllerRef = useRef<AbortController | null>(null)
  const bookingsControllerRef = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    residencesControllerRef.current?.abort()
    const controller = new AbortController()
    residencesControllerRef.current = controller

    const result = await listResidences({ signal: controller.signal })
    if (controller.signal.aborted) {
      return
    }
    if (!result.ok) {
      setResidencesState({ phase: 'error', error: result.error })
      return
    }
    setResidencesState({ phase: 'ready', residences: result.data })
    const first = result.data[0]
    setResidenceId(first === undefined ? null : first.id)
  }, [])

  /**
   * Twee aanvragen in plaats van één ongefilterde lijst: het endpoint filtert op
   * status, en zo komen geannuleerde en verlopen reserveringen niet mee die op dit
   * scherm niets te zoeken hebben. Ze lopen parallel, dus het kost geen wachttijd.
   */
  const loadBookings = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      setBookingsState({ phase: 'idle' })
      return
    }
    bookingsControllerRef.current?.abort()
    const controller = new AbortController()
    bookingsControllerRef.current = controller

    const [pending, confirmed] = await Promise.all([
      listBookings(residenceId, { status: 'hold', signal: controller.signal }),
      listBookings(residenceId, { status: 'confirmed', signal: controller.signal }),
    ])
    if (controller.signal.aborted) {
      return
    }
    if (!pending.ok) {
      setBookingsState({ phase: 'error', error: pending.error })
      return
    }
    if (!confirmed.ok) {
      setBookingsState({ phase: 'error', error: confirmed.error })
      return
    }
    setBookingsState({
      phase: 'ready',
      lists: {
        pending: sortByDeadline(pending.data),
        confirmed: sortByArrival(confirmed.data),
      },
    })
  }, [residenceId])

  useEffect(() => {
    // Zie de toelichting op hetzelfde patroon in media-page.tsx: state wordt pas
    // ná de await gezet, de begintoestand is al 'loading', en de cleanup breekt de
    // aanvraag af. De lintregel kan dat statisch niet zien.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
    return () => {
      residencesControllerRef.current?.abort()
    }
  }, [loadResidences])

  useEffect(() => {
    if (residenceId === null) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBookingsState({ phase: 'loading' })
    void loadBookings()
    return () => {
      bookingsControllerRef.current?.abort()
    }
  }, [residenceId, loadBookings])

  /**
   * Voert één actie uit en haalt daarna opnieuw op — ook wanneer de actie
   * mislukte. Bij een conflict (409) is dat het punt: de aanvraag is elders al
   * afgehandeld, en de verse lijst laat zien wat er werkelijk staat.
   */
  const runAction = useCallback(
    async (run: () => Promise<BookingResult<unknown>>): Promise<void> => {
      setBusy(true)
      setActionError(null)
      const result = await run()
      if (!result.ok) {
        setActionError(result.error)
      }
      await loadBookings()
      setBusy(false)
    },
    [loadBookings],
  )

  const handleConfirm = useCallback(
    (booking: Booking, input: ConfirmInput): void => {
      void runAction(() => confirmBooking(booking.residenceId, booking.id, input))
    },
    [runAction],
  )

  const handleDecline = useCallback(
    (booking: Booking, reason: string): void => {
      void runAction(() => declineBooking(booking.residenceId, booking.id, reason))
    },
    [runAction],
  )

  const handleExtend = useCallback(
    (booking: Booking, hours: number): void => {
      void runAction(() => extendBooking(booking.residenceId, booking.id, hours))
    },
    [runAction],
  )

  /**
   * Annuleren, en daarna tonen wat de gast terugkrijgt.
   *
   * Het bedrag blijft op het scherm staan tot de verhuurder verder klikt: het is
   * het enige moment waarop hij het ziet, en het platform betaalt niet voor hem
   * terug (§17.11). Een melding die vanzelf verdwijnt zou hier een bedrag zijn dat
   * niemand heeft gelezen.
   */
  const handleCancel = useCallback(
    (booking: Booking, reason: string): void => {
      void (async () => {
        setBusy(true)
        setActionError(null)
        setRefund(null)
        const result = await cancelBooking(booking.residenceId, booking.id, reason)
        if (result.ok) {
          // Geen voorwaarde bij het boeken levert `null` op; dat is een eigen
          // uitkomst en niet "nul terug", en de melding zegt het ook anders.
          setRefund(
            result.data.refund ?? {
              amountCents: 0,
              nightsRefundPermille: 0,
              cleaningRefunded: false,
              reason: 'no_policy',
            },
          )
          setRefundCurrency(result.data.booking.price.currency)
        } else {
          setActionError(result.error)
        }
        await loadBookings()
        setBusy(false)
      })()
    },
    [loadBookings],
  )

  const lists = useMemo<BookingLists>(
    () => (bookingsState.phase === 'ready' ? bookingsState.lists : { pending: [], confirmed: [] }),
    [bookingsState],
  )

  return (
    <section className="page" aria-labelledby="bookings-heading">
      <div className="page__header">
        <h1 className="page__title" id="bookings-heading">
          {t('dashboard.bookings.title')}
        </h1>
        <button
          className="button"
          disabled={residenceId === null || bookingsState.phase === 'loading' || busy}
          onClick={() => {
            setBookingsState({ phase: 'loading' })
            void loadBookings()
          }}
          type="button"
        >
          {t('common.action.reload')}
        </button>
      </div>

      {residencesState.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {residencesState.phase === 'error' && <FailureNotice error={residencesState.error} />}

      {residencesState.phase === 'ready' && residencesState.residences.length === 0 && (
        <p className="page__status" role="status">
          {t('dashboard.bookings.noResidence')}
        </p>
      )}

      {/* De keuzelijst verschijnt pas bij meer dan één residence; bij precies één
          is er niets te kiezen en zou hij alleen ruis zijn. */}
      {residencesState.phase === 'ready' && residencesState.residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.bookings.residence')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residencesState.residences.map((residence) => (
              <option key={residence.id} value={residence.id}>
                {/* Naam uit de database: data, geen interfacetekst. */}
                {residence.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {actionError !== null && <FailureNotice error={actionError} />}

      {refund !== null && (
        <div className="notice notice--action" role="status">
          <p className="notice__title">
            {refund.reason === 'no_policy'
              ? t('dashboard.bookings.refund.noPolicy')
              : t('dashboard.bookings.refund.due', {
                  amount: formatters.formatMoney(refund.amountCents, refundCurrency),
                })}
          </p>
          <p className="notice__detailText">{t(`dashboard.bookings.refund.${refund.reason}`)}</p>
          <button
            className="button button--quiet"
            onClick={() => {
              setRefund(null)
            }}
            type="button"
          >
            {t('dashboard.bookings.refund.dismiss')}
          </button>
        </div>
      )}

      {bookingsState.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('common.state.loading')}
        </p>
      )}

      {bookingsState.phase === 'error' && <FailureNotice error={bookingsState.error} />}

      {bookingsState.phase === 'ready' && (
        <>
          <section className="worklist" aria-labelledby="bookings-pending-heading">
            <div className="worklist__header">
              <h2 className="worklist__title" id="bookings-pending-heading">
                {t('dashboard.bookings.pending.title')}
              </h2>
              <p className="worklist__count" role="status">
                {t('dashboard.bookings.pending.count', { count: lists.pending.length })}
              </p>
            </div>
            <p className="worklist__intro">{t('dashboard.bookings.pending.intro')}</p>

            {lists.pending.length === 0 ? (
              <p className="page__status">{t('dashboard.bookings.pending.empty')}</p>
            ) : (
              <ul className="worklist__items">
                {lists.pending.map((booking) => (
                  <RequestCard
                    booking={booking}
                    busy={busy}
                    key={booking.id}
                    now={now}
                    onConfirm={handleConfirm}
                    onDecline={handleDecline}
                    onExtend={handleExtend}
                  />
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="bookings-confirmed-heading">
            <h2 className="worklist__title" id="bookings-confirmed-heading">
              {t('dashboard.bookings.confirmed.title')}
            </h2>

            {lists.confirmed.length === 0 ? (
              <p className="page__status">{t('dashboard.bookings.confirmed.empty')}</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('dashboard.bookings.column.dates')}</th>
                    <th scope="col">{t('dashboard.bookings.column.guest')}</th>
                    <th scope="col">{t('dashboard.bookings.column.guests')}</th>
                    <th scope="col">{t('dashboard.bookings.column.total')}</th>
                    <th scope="col">{t('dashboard.bookings.column.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lists.confirmed.map((booking) => (
                    <ConfirmedRow
                      booking={booking}
                      busy={busy}
                      key={booking.id}
                      onCancel={handleCancel}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </section>
  )
}
